import React, { useState } from "react";
import Image from "next/legacy/image";
import useSWR, { SWRConfig } from "swr";
import Link from "@/utils/link";
import moment from "moment";

import HomeHeader from "@/components/home/HomeHeader";
import ArticleCard from "@/components/article/ArticleCard";
import { fetchGraphql } from "@/utils/query";

import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import { imageLoader } from "@/utils/image";
import TextField from "@/ui/TextField";
import Button from "@/ui/Button";
import { removeExt } from "@/utils/image";

const fetcher = (url) => fetch(url).then((res) => res.json());

const HorizontalArticleCard = ({ article, imageHeight = "h-48 w-96" }) => {
    return (
        <div className="bg-gray-200/50 hover:bg-gray-200/80">
            <Link href={"/resources/articles/" + article.articleId}>
                <a className="flex">
                    {article.mainImageUrl ? (
                        <div className={`relative ${imageHeight} `}>
                            <Image
                                loader={imageLoader}
                                src={removeExt(article.mainImageUrl) + ".md.jpg"}
                                alt={article.title}
                                layout="fill"
                                objectFit="cover"
                                objectPosition="center"
                                priority
                                unoptimized={false}
                            />
                        </div>
                    ) : (
                        <div className={`w-full ${imageHeight} bg-gray-200`}></div>
                    )}

                    <div className="flex flex-col justify-between w-full px-8 pt-4">
                        <div>
                            <div className="flex items-center justify-between line-clamp-2">
                                <h3 className="text-xl font-headline font-bold text-primary-500">
                                    {article.title}
                                </h3>

                                {/*<h4 className="text-sm font-bold">
                                    {moment(article.date).isValid()
                                        ? moment(article.date).format("MMM DD, YYYY")
                                        : ""}
                                    </h4>*/}
                            </div>

                            <div className="mt-2 line-clamp-2 font-headline font-bold text-sm text-gray-500 uppercase">
                                {article.topic &&
                                    article.topic.split(";").map((k, i) => (
                                        <span key={k}>
                                            {i > 0 ? (
                                                <span className="mx-2">&bull;</span>
                                            ) : null}
                                            {k}
                                        </span>
                                    ))}

                                {/*moment(article._createdAt).isValid()
                                    ? moment(article._createdAt).format(" MMM DD, YYYY")
                                    : ""*/}
                            </div>

                            <p className="mt-4 line-clamp-2 lg:line-clamp-3">
                                {article.description}
                            </p>
                        </div>

                        {/*<div className="text-sm font-extrabold uppercase text-primary-500">
                            Read More
                        </div>*/}
                    </div>
                </a>
            </Link>
        </div>
    );
};

const VerticalArticleCard = ({ article, imageHeight = "h-32 w-full" }) => {
    return (
        <div className="bg-gray-200/50 hover:bg-gray-200/80">
            <Link href={"/resources/articles/" + article.articleId}>
                <a className="flex flex-col">
                    {article.mainImageUrl ? (
                        <div className={`relative ${imageHeight} `}>
                            <Image
                                loader={imageLoader}
                                src={removeExt(article.mainImageUrl) + ".md.jpg"}
                                alt={article.title}
                                layout="fill"
                                objectFit="cover"
                                objectPosition="center"
                                priority
                                unoptimized={false}
                            />
                        </div>
                    ) : (
                        <div className={`w-full ${imageHeight} bg-gray-200`}></div>
                    )}

                    <div className="flex flex-col justify-between w-full px-4 py-4">
                        <div>
                            <div className="flex items-center justify-between line-clamp-2">
                                <h3 className="text-xl font-headline font-bold text-primary-500">
                                    {article.title}
                                </h3>

                                {/*<h4 className="text-sm font-bold">
                                    {moment(article.date).isValid()
                                        ? moment(article.date).format("MMM DD, YYYY")
                                        : ""}
                                    </h4>*/}
                            </div>

                            <div className="mt-2 line-clamp-3 font-headline font-bold text-sm text-gray-500 uppercase">
                                {article.topic &&
                                    article.topic.split(";").map((k, i) => (
                                        <span key={k}>
                                            {i > 0 ? (
                                                <span className="mx-2">&bull;</span>
                                            ) : null}
                                            {k}
                                        </span>
                                    ))}
                            </div>

                            <p className="mt-4 line-clamp-3 lg:line-clamp-3">
                                {article.description}
                            </p>
                        </div>

                        <div className="mt-4 text-sm font-extrabold uppercase text-primary-500">
                            Read More
                        </div>
                    </div>
                </a>
            </Link>
        </div>
    );
};

const ArticlePage = ({ fallback }) => {
    return (
        <SWRConfig value={{ fallback }}>
            <ArticlePageWithInitialData />
        </SWRConfig>
    );
};

const ArticlePageWithInitialData = () => {
    const { data } = useSWR("/api/article", fetcher);
    const [search, setSearch] = useState("");

    return (
        <div className="h-screen bg-gray-50">
            <HomeHeader fixed />

            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-16">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        { name: "Articles", href: "/resources/articles" },
                    ]}
                />
            </div>

            <div className="">
                <div className="hidden sm:block relative w-full h-40 sm:h-96 bg-black">
                    <Image
                        loader={imageLoader}
                        src="https://cdn.purpledoorfinders.com/purpledoorfinders/purpledoorfinders-articles"
                        alt="purple-door-finders-articles"
                        layout="fill"
                        objectFit="cover"
                        objectPosition="0px -200px"
                        priority
                        unoptimized={false}
                        className="opacity-80"
                    />

                    <div className="flex justify-center">
                        <h1 className="absolute px-8 top-12 sm:top-48 text-3xl sm:text-5xl text-white font-headline font-bold">
                            Articles
                        </h1>
                    </div>
                </div>

                <div className="container mx-auto mt-8 mb-12 px-4 sm:px-16">
                    <div className="flex justify-start items-center mb-8">
                        <TextField
                            autoFocus={true}
                            placeHolder="Search"
                            fullwidth
                            className="w-[400px]"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search ? (
                            <Button
                                className="w-40 ml-4 mt-1"
                                onClick={() => setSearch("")}
                            >
                                Clear Search
                            </Button>
                        ) : null}
                    </div>

                    <div className="flex flex-col space-y-8">
                        {data
                            .filter((article) => {
                                if (search) {
                                    if (article.topic) {
                                        if (
                                            article.topic
                                                .toLowerCase()
                                                .includes(search.toLowerCase())
                                        ) {
                                            return true;
                                        }
                                    }

                                    if (article.title) {
                                        if (
                                            article.title
                                                .toLowerCase()
                                                .includes(search.toLowerCase())
                                        ) {
                                            return true;
                                        }
                                    }

                                    return false;
                                } else {
                                    return true;
                                }
                            })
                            .map((article) => (
                                <div key={article.articleId}>
                                    <div className="hidden sm:block">
                                        <HorizontalArticleCard article={article} />
                                    </div>

                                    <div className="block sm:hidden">
                                        <VerticalArticleCard article={article} />
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>

            <SeoLinks />
            <HomeFooter />
        </div>
    );
};

//export const getStaticProps = async () => {
export const getServerSideProps = async () => {
    const result = await fetchGraphql(`
        query {
            articles {
                articleId
                _createdAt
                _updatedAt
                type
                newsroomType
                title
                date
                description
                mainImageUrl
                topic
                author_link {
                    name
                }
            }
        }
    `);

    return {
        props: {
            fallback: {
                "/api/article":
                    result.data.articles && result.data.articles.length > 0
                        ? result.data.articles
                              .filter(
                                  (a) =>
                                      a.newsroomType !== "Newsroom" &&
                                      a.newsroomType !== "Endorsement"
                              )
                              .sort(
                                  (a, b) => new Date(b._createdAt) - new Date(a._createdAt)
                              )
                        : [],
            },
        },
    };
};

export default ArticlePage;
